import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from "react";
import ReactPlayer from "react-player";
import { FaPlay, FaPause } from "react-icons/fa";
import { toast } from "react-toastify";
import { parseClipTimestamp, formatClipTimestamp, isClipRangeValid } from "./clipTimeUtils";
import { classifyVideoUrl, isReactPlayerControllable, isRemoteShareLink, sharePlaybackUrl } from "./videoPlayback";
import api from "../../api";
import MobileClipControls from "./MobileClipControls";
import { CropOverlay, CropPanel } from "./CropStudio";
import PlayerControls from "./PlayerControls";
import {
  ASPECT_PRESETS,
  CROP_EXIT_MS,
  DEFAULT_SOURCE_SIZE,
  centeredRect,
  cropToRect,
  fitFrame,
  fitRectToRatio,
  rectToCrop,
  sameBox,
  toNormRatio,
} from "./cropUtils";
import "./mobile-clip-controls.css";
/** Set false to revert mobile clip studio (buttons + scrub bar); double-tap/Enter unchanged */
const ENABLE_MOBILE_CLIP_STUDIO = true;

const PLAYER_CONFIG = {
  youtube: {
    playerVars: {
      autoplay: 0,
      playsinline: 1,
      // YouTube's own UI is replaced by <PlayerControls> + an input shield
      controls: 0,
      disablekb: 1,
      enablejsapi: 1,
      rel: 0,
      modestbranding: 1,
      fs: 0,
      iv_load_policy: 3,
      cc_load_policy: 0,
      origin: typeof window !== 'undefined' ? window.location.origin : undefined,
    },
  },
  file: {
    attributes: {
      playsInline: true,
      "webkit-playsinline": "true",
      controlsList: "nodownload",
      preload: "metadata",
    },
  },
};

const isYouTubeUrl = (url) => /(?:youtube\.com|youtu\.be)/i.test(url || '');

const Video = forwardRef(
  ({ videoUrl, clips = [], onClipSave, character, onPhaseChange, onCropApply, clipEditTarget, onApplyClipTimeFromVideo, onClipEditPlayhead, onCancelClipEdit, onClipRangeEnd, onDurationReady, playerLabel = '', comparePlayer = false, keyboardSeekEnabled = true }, ref) => {
    const playbackUrl = useMemo(() => sharePlaybackUrl(videoUrl), [videoUrl]);
    const playback = useMemo(() => classifyVideoUrl(playbackUrl), [playbackUrl]);
    const playerControllable = useMemo(() => isReactPlayerControllable(playbackUrl), [playbackUrl]);
    const resolvedDurationRef = useRef(0);

    const playerRef = useRef(null);
    const playerWrapperRef = useRef(null);
    const clickCountRef = useRef(0);
    const startTimeRef = useRef(null);
    const rangePreviewRef = useRef(null);
    const onClipRangeEndRef = useRef(onClipRangeEnd);
    onClipRangeEndRef.current = onClipRangeEnd;

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [shouldAutoplay, setShouldAutoplay] = useState(false);
    const [isPlayingRange, setIsPlayingRange] = useState(false);
    const [isEditingPlayback, setIsEditingPlayback] = useState(false);
    /* ── Crop state ───────────────────────────────────────────────
     * The player is never unmounted for cropping: the overlay and panel
     * are layered on top/below, so playback continues uninterrupted.
     * cropMode: 'off' | 'open' | 'closing' (closing = exit animation). */
    const [cropMode, setCropMode] = useState('off');
    const isCropping = cropMode === 'open';
    const cropVisible = cropMode !== 'off';
    const isCroppingRef = useRef(false);
    isCroppingRef.current = isCropping;
    const cropCloseTimerRef = useRef(null);
    const [cropRect, setCropRect] = useState(null);
    const [cropAspect, setCropAspect] = useState('free');
    const [cropTargets, setCropTargets] = useState([]);
    const [frameBox, setFrameBox] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    // Custom controls state (replaces YouTube / native controls)
    const rootRef = useRef(null);
    const [duration, setDuration] = useState(0);
    const [buffered, setBuffered] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [flash, setFlash] = useState(null);

    const [embedSize, setEmbedSize] = useState({ width: 360, height: 203 });
    const [isCoarsePointer, setIsCoarsePointer] = useState(false);
    const [clipTouchEnabled, setClipTouchEnabled] = useState(false);
    const [showMobilePlay, setShowMobilePlay] = useState(false);
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [markStep, setMarkStep] = useState(0);
    const [mobileFeedback, setMobileFeedback] = useState(null);
    const [sourceSize, setSourceSize] = useState(DEFAULT_SOURCE_SIZE);
    const sourceSizeRef = useRef(DEFAULT_SOURCE_SIZE);
    sourceSizeRef.current = sourceSize;

    // ── Dimension calculation ─────────────────────────────────────
    const readSourceSize = useCallback(() => {
      try {
        const internal = playerRef.current?.getInternalPlayer?.();
        if (internal?.videoWidth > 0 && internal?.videoHeight > 0) {
          return { width: internal.videoWidth, height: internal.videoHeight };
        }
      } catch {
        // YouTube iframes don't expose resolution — keep 16:9 reference
      }
      return null;
    }, []);

    const calculateDimensions = useCallback(() => {
      const wrapper = playerWrapperRef.current;
      if (!wrapper) return;

      const bounds = wrapper.getBoundingClientRect();
      let playerWidth = bounds.width;
      if (playerWidth <= 0) {
        playerWidth = wrapper.parentElement?.getBoundingClientRect().width || 0;
      }
      if (playerWidth <= 0) {
        playerWidth = Math.min(window.innerWidth - 32, 640);
      }

      const nextEmbed = {
        width: Math.floor(playerWidth),
        height: Math.max(Math.round(playerWidth * (9 / 16)), 180),
      };
      setEmbedSize((prev) => (
        prev.width === nextEmbed.width && prev.height === nextEmbed.height ? prev : nextEmbed
      ));

      const detected = readSourceSize();
      if (detected) {
        setSourceSize((prev) => (
          prev.width === detected.width && prev.height === detected.height ? prev : detected
        ));
      }
      const src = detected || sourceSizeRef.current;
      const playerHeight = bounds.height || nextEmbed.height;
      const nextFrame = fitFrame(playerWidth, playerHeight, src.width, src.height);
      setFrameBox((prev) => (sameBox(prev, nextFrame) ? prev : nextFrame));
    }, [readSourceSize]);

    useEffect(() => {
      setSourceSize(DEFAULT_SOURCE_SIZE);
    }, [videoUrl]);

    useEffect(() => {
      const fallbackW = Math.min(window.innerWidth - 32, 640);
      setEmbedSize({
        width: fallbackW,
        height: Math.max(Math.round(fallbackW * (9 / 16)), 180),
      });
      calculateDimensions();
      const t1 = setTimeout(calculateDimensions, 100);
      const t2 = setTimeout(calculateDimensions, 400);

      window.addEventListener("resize", calculateDimensions);
      const vv = window.visualViewport;
      vv?.addEventListener("resize", calculateDimensions);

      const wrapper = playerWrapperRef.current;
      let ro;
      if (wrapper && typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => calculateDimensions());
        ro.observe(wrapper);
      }

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        window.removeEventListener("resize", calculateDimensions);
        vv?.removeEventListener("resize", calculateDimensions);
        ro?.disconnect();
      };
    }, [calculateDimensions, videoUrl]);

    // Picture position depends on source aspect ratio — re-fit when it changes
    useEffect(() => {
      calculateDimensions();
    }, [sourceSize, cropVisible, calculateDimensions]);

    useEffect(() => {
      resolvedDurationRef.current = 0;
      if (!isRemoteShareLink(videoUrl)) return undefined;
      let cancelled = false;
      api.post('/dreamface/resolve-preview', { url: videoUrl })
        .then((res) => {
          const duration = Number(res.data?.duration || 0);
          if (cancelled || !(duration > 0.05)) return;
          resolvedDurationRef.current = duration;
          onDurationReady?.(duration);
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }, [videoUrl, onDurationReady]);

    useEffect(() => {
      const coarse = window.matchMedia("(hover: none) and (pointer: coarse)");
      const narrow = window.matchMedia("(max-width: 900px)");
      const apply = () => {
        setIsCoarsePointer(coarse.matches);
        setClipTouchEnabled(coarse.matches || narrow.matches);
        setShouldAutoplay(!coarse.matches);
      };
      apply();
      coarse.addEventListener?.("change", apply);
      narrow.addEventListener?.("change", apply);
      return () => {
        coarse.removeEventListener?.("change", apply);
        narrow.removeEventListener?.("change", apply);
      };
    }, []);

    useEffect(() => {
      setShowMobilePlay(isCoarsePointer && isYouTubeUrl(videoUrl));
    }, [videoUrl, isCoarsePointer]);

    // ── Crop ──────────────────────────────────────────────────────
    const normRatio = useMemo(() => {
      const preset = ASPECT_PRESETS.find((p) => p.id === cropAspect);
      return preset?.ratio ? toNormRatio(preset.ratio, sourceSize) : null;
    }, [cropAspect, sourceSize]);

    /**
     * Open crop mode without touching playback.
     * options.clipIndices — clips preselected as targets (default: last clip)
     * options.crop        — crop to start from (default: first target's crop)
     */
    const openCrop = useCallback((options = {}) => {
      if (!playerControllable) return false;
      clearTimeout(cropCloseTimerRef.current);
      const count = clips.length;
      let targets = Array.isArray(options.clipIndices)
        ? options.clipIndices.filter((i) => Number.isInteger(i) && i >= 0 && i < count)
        : [];
      if (!targets.length && count) targets = [count - 1];

      const existing = options.crop || targets.map((i) => clips[i]?.crop).find(Boolean) || null;
      const restored = existing ? cropToRect(existing, sourceSizeRef.current) : null;

      setCropTargets(targets);
      setCropAspect('free');
      setCropRect(restored || centeredRect(null));
      setCropMode('open');
      calculateDimensions();
      requestAnimationFrame(() => {
        playerWrapperRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      });
      return true;
    }, [clips, playerControllable, calculateDimensions]);

    const closeCrop = useCallback(() => {
      clearTimeout(cropCloseTimerRef.current);
      setCropMode((mode) => (mode === 'off' ? 'off' : 'closing'));
      cropCloseTimerRef.current = setTimeout(() => {
        setCropMode('off');
        setCropRect(null);
      }, CROP_EXIT_MS);
    }, []);

    useEffect(() => () => clearTimeout(cropCloseTimerRef.current), []);

    useEffect(() => {
      clearTimeout(cropCloseTimerRef.current);
      setCropMode('off');
      setCropRect(null);
    }, [videoUrl]);

    // Drop targets that no longer exist (clip deleted while cropping)
    useEffect(() => {
      setCropTargets((prev) => {
        const next = prev.filter((i) => i < clips.length);
        return next.length === prev.length ? prev : next;
      });
    }, [clips.length]);

    const cropPayload = useMemo(
      () => (cropRect ? rectToCrop(cropRect, sourceSize, videoUrl) : null),
      [cropRect, sourceSize, videoUrl]
    );

    const handleAspectChange = useCallback((id) => {
      setCropAspect(id);
      const preset = ASPECT_PRESETS.find((p) => p.id === id);
      const nr = preset?.ratio ? toNormRatio(preset.ratio, sourceSizeRef.current) : null;
      if (!nr) return;
      setCropRect((r) => (r ? fitRectToRatio(r, nr) : centeredRect(nr)));
    }, []);

    const resetCrop = useCallback(() => {
      setCropRect(centeredRect(normRatio));
    }, [normRatio]);

    const applyCrop = useCallback(() => {
      if (!cropPayload || !cropTargets.length) return;
      onCropApply?.(cropPayload, [...cropTargets]);
      closeCrop();
    }, [cropPayload, cropTargets, onCropApply, closeCrop]);

    const applyCropRef = useRef(applyCrop);
    applyCropRef.current = applyCrop;

    const removeCrop = useCallback(() => {
      const withCrop = cropTargets.filter((i) => clips[i]?.crop);
      if (!withCrop.length) return;
      onCropApply?.(null, withCrop);
      closeCrop();
    }, [cropTargets, clips, onCropApply, closeCrop]);

    const copyCrop = useCallback(() => {
      if (!cropPayload) return;
      const text = JSON.stringify(cropPayload, null, 2);
      if (!navigator.clipboard?.writeText) {
        toast.error("Clipboard not available");
        return;
      }
      navigator.clipboard.writeText(text)
        .then(() => toast.success("Crop copied to clipboard"))
        .catch(() => toast.error("Could not copy crop"));
    }, [cropPayload]);

    const pauseInternalPlayer = useCallback(() => {
      try {
        const internal = playerRef.current?.getInternalPlayer();
        if (internal?.pauseVideo) internal.pauseVideo();
        else if (typeof internal?.pause === 'function') internal.pause();
      } catch {
        // player not ready
      }
    }, []);

    const stopClipRangePreview = useCallback((notifyEnd = false) => {
      const wasActive = !!rangePreviewRef.current;
      rangePreviewRef.current = null;
      setIsPlayingRange(false);
      setIsEditingPlayback(false);
      if (wasActive) {
        pauseInternalPlayer();
        if (notifyEnd) onClipRangeEndRef.current?.();
      }
    }, [pauseInternalPlayer]);

    const pausePlayback = useCallback(() => {
      rangePreviewRef.current = null;
      setIsPlayingRange(false);
      setIsEditingPlayback(false);
      pauseInternalPlayer();
    }, [pauseInternalPlayer]);

    const seekAndPlayAt = useCallback((timeString) => {
      if (!playerControllable) {
        return { ok: false, error: 'Seek is not available for embedded page videos — use manual timestamps.' };
      }
      const player = playerRef.current;
      if (!player) return { ok: false, error: 'Player not ready' };

      const seconds = parseClipTimestamp(timeString);
      if (seconds == null) return { ok: false, error: 'Invalid timestamp format' };

      const duration = player.getDuration();
      const target = duration > 0 ? Math.min(seconds, duration) : seconds;
      rangePreviewRef.current = null;
      setIsPlayingRange(false);
      setIsEditingPlayback(false);
      player.seekTo(target, 'seconds', true);

      return { ok: true, seconds: target };
    }, [playerControllable]);

    const playClipRange = useCallback((startTimeString, endTimeString, options = {}) => {
      if (!playerControllable) {
        return { ok: false, error: 'Clip preview is not available for embedded page videos.' };
      }
      const player = playerRef.current;
      if (!player) return { ok: false, error: 'Player not ready' };

      const startSec = parseClipTimestamp(startTimeString);
      const endSec = parseClipTimestamp(endTimeString);
      if (startSec == null || endSec == null) {
        return { ok: false, error: 'Invalid timestamp format' };
      }
      if (!isClipRangeValid(startTimeString, endTimeString)) {
        return { ok: false, error: 'Start time must be before end time' };
      }

      const duration = player.getDuration();
      const clampedStart = duration > 0 ? Math.min(startSec, duration) : startSec;
      const clampedEnd = duration > 0 ? Math.min(endSec, duration) : endSec;

      rangePreviewRef.current = {
        startSec: clampedStart,
        endSec: clampedEnd,
        loop: !!options.loop,
      };

      setIsEditingPlayback(false);
      setIsPlayingRange(true);
      player.seekTo(clampedStart, 'seconds', true);
      const internal = player.getInternalPlayer();
      if (internal?.playVideo) internal.playVideo();
      else if (typeof internal?.play === 'function') internal.play().catch(() => {});

      return { ok: true, startSec: clampedStart, endSec: clampedEnd };
    }, [playerControllable]);

    useImperativeHandle(ref, () => ({
      resetClipState: () => {
        clickCountRef.current = 0;
        startTimeRef.current = null;
        setStartTime(null);
        setEndTime(null);
        setMarkStep(0);
        setMobileFeedback(null);
        if (onPhaseChange) onPhaseChange(true);
      },

      processManualEntry: (timeString) => {
        const isValid = /^\d{2}:\d{2}:\d{2}\.\d{2}$/.test(timeString);
        if (!isValid) {
          return { error: "Invalid format. Use HH:MM:SS.ms" };
        }

        if (clickCountRef.current === 0) {
          setStartTime(timeString);
          startTimeRef.current = timeString;
          onClipSave(timeString, null, character);
          clickCountRef.current = 1;
          setMarkStep(1);
          if (onPhaseChange) onPhaseChange(false);
          return { success: "Start time saved" };
        }

        if (clickCountRef.current === 1) {
          const start = startTimeRef.current || startTime;
          if (!isClipRangeValid(start, timeString)) {
            return { error: "Start time must be before end time" };
          }
          setEndTime(timeString);
          onClipSave(start, timeString, character);
          clickCountRef.current = 2;
          setMarkStep(0);

          setTimeout(() => {
            clickCountRef.current = 0;
            startTimeRef.current = null;
            setStartTime(null);
            setEndTime(null);
            setMobileFeedback(null);
            if (onPhaseChange) onPhaseChange(true);
          }, 100);

          return { success: "End time saved" };
        }

        return { error: "Both times already set. Reset or wait." };
      },

      seekToTimestamp: (timeString) => {
        if (!playerControllable) {
          return { ok: false, error: "Seek is not available for embedded page videos — use manual timestamps." };
        }
        const player = playerRef.current;
        if (!player) return { ok: false, error: "Player not ready" };

        const seconds = parseClipTimestamp(timeString);
        if (seconds == null) return { ok: false, error: "Invalid timestamp format" };

        const duration = player.getDuration();
        const target = duration > 0 ? Math.min(seconds, duration) : seconds;
      rangePreviewRef.current = null;
      setIsPlayingRange(false);
      setIsEditingPlayback(false);
      player.seekTo(target, "seconds", true);
      if (comparePlayer) {
        pauseInternalPlayer();
        setTimeout(pauseInternalPlayer, 80);
      }
      return { ok: true, seconds: target };
      },

      seekAndPlayAt,

      /** Exit timestamp edit / clip-preview UI without forcing pause (restore pre-preview behavior). */
      releaseTimestampEdit: () => {
        setIsEditingPlayback(false);
        rangePreviewRef.current = null;
        setIsPlayingRange(false);
      },

      getPlayheadTimestamp: () => {
        if (!playerControllable) return null;
        const player = playerRef.current;
        if (!player) return null;
        return formatClipTimestamp(player.getCurrentTime());
      },

      playClipRange,
      stopClipRangePreview,
      pausePlayback,
      getDurationSeconds: () => {
        const fromPlayer = Number(playerRef.current?.getDuration?.() || 0);
        if (Number.isFinite(fromPlayer) && fromPlayer > 0.05) return fromPlayer;
        const fromMeta = Number(resolvedDurationRef.current || 0);
        return Number.isFinite(fromMeta) && fromMeta > 0.05 ? fromMeta : 0;
      },

      /** Open crop mode, e.g. from a clip card: openCrop({ clipIndices: [2] }) */
      openCrop,
      closeCrop,
      isCropping: () => isCroppingRef.current,

      player: playerRef.current,
    }));

    const formatTime = (seconds) => {
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      const ms = Math.floor((seconds % 1) * 100);
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(2, "0")}`;
    };

    const isTypingTarget = (target) => {
      if (!target) return false;
      const tag = target.tagName || "";
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
    };

    const togglePlayPause = useCallback(() => {
      const player = playerRef.current;
      if (!player) return;
      try {
        const internal = player.getInternalPlayer();
        if (!internal) return;
        const youtubePlaying = typeof internal.getPlayerState === "function" && internal.getPlayerState() === 1;
        const filePlaying = typeof internal.paused === "boolean" && !internal.paused;
        if (youtubePlaying || filePlaying) {
          if (internal.pauseVideo) internal.pauseVideo();
          else internal.pause?.();
          return;
        }
        if (internal.playVideo) internal.playVideo();
        else internal.play?.();
      } catch {
        // player not ready
      }
    }, []);

    const applyPlayheadToTarget = useCallback(() => {
      if (!playerRef.current || !clipEditTarget || !onApplyClipTimeFromVideo) return false;
      const formatted = formatClipTimestamp(playerRef.current.getCurrentTime());
      if (!formatted) return false;
      onApplyClipTimeFromVideo(formatted);
      return true;
    }, [clipEditTarget, onApplyClipTimeFromVideo]);

    const resetClipMarker = useCallback(() => {
      clickCountRef.current = 0;
      startTimeRef.current = null;
      setStartTime(null);
      setEndTime(null);
      setMarkStep(0);
      setMobileFeedback(null);
      if (onPhaseChange) onPhaseChange(true);
    }, [onPhaseChange]);

    const markClipAtPlayhead = useCallback((options = {}) => {
      const { feedbackPrefix } = options;
      if (!playerControllable) {
        if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) {
          setMobileFeedback('Use manual timestamps below — embedded pages cannot report playhead position.');
        }
        return false;
      }
      if (!playerRef.current) return false;

      if (clipEditTarget && onApplyClipTimeFromVideo) {
        applyPlayheadToTarget();
        return true;
      }

      const player = playerRef.current;
      const current = player.getCurrentTime();
      const formatted = formatTime(current);

      if (clickCountRef.current === 0) {
        setStartTime(formatted);
        startTimeRef.current = formatted;
        onClipSave(formatted, null, character);
        clickCountRef.current = 1;
        setMarkStep(1);
        if (onPhaseChange) onPhaseChange(false);
        if (feedbackPrefix !== false && clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) {
          setMobileFeedback(`Start saved: ${formatted}`);
        }
        return true;
      }

      if (clickCountRef.current === 1) {
        const start = startTimeRef.current || startTime;
        if (!isClipRangeValid(start, formatted)) {
          if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) {
            setMobileFeedback('End must be after start — scrub forward and try again.');
          }
          return false;
        }

        setEndTime(formatted);
        onClipSave(start, formatted, character);
        clickCountRef.current = 2;
        if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) {
          setMobileFeedback(`Clip saved: ${start} → ${formatted}`);
        }
        setTimeout(() => {
          clickCountRef.current = 0;
          startTimeRef.current = null;
          setStartTime(null);
          setEndTime(null);
          setMarkStep(0);
          setMobileFeedback(null);
          if (onPhaseChange) onPhaseChange(true);
        }, 100);
        return true;
      }

      return false;
    }, [
      character,
      clipEditTarget,
      onApplyClipTimeFromVideo,
      applyPlayheadToTarget,
      onClipSave,
      onPhaseChange,
      startTime,
      clipTouchEnabled,
      playerControllable,
    ]);

    const editingActiveRef = useRef(false);
    editingActiveRef.current = Boolean(clipEditTarget);

    const reportPlayheadIfEditing = useCallback((seconds) => {
      if (!editingActiveRef.current || !onClipEditPlayhead) return;
      const formatted = formatClipTimestamp(seconds);
      if (formatted) onClipEditPlayhead(formatted);
    }, [onClipEditPlayhead]);

    const seekPlayheadBySeconds = useCallback((deltaSec) => {
      const player = playerRef.current;
      if (!player) return;

      const current = player.getCurrentTime();
      const duration = player.getDuration();
      const next = deltaSec < 0
        ? Math.max(current + deltaSec, 0)
        : Math.min(current + deltaSec, duration > 0 ? duration : current + deltaSec);

      const timeString = formatClipTimestamp(next);
      if (!timeString) return;

      seekAndPlayAt(timeString);
      reportPlayheadIfEditing(next);
    }, [seekAndPlayAt, reportPlayheadIfEditing]);

    const handleMobileSetStart = useCallback(() => {
      if (markStep !== 0) return;
      markClipAtPlayhead();
    }, [markStep, markClipAtPlayhead]);

    const handleMobileSetEnd = useCallback(() => {
      if (markStep !== 1) return;
      markClipAtPlayhead();
    }, [markStep, markClipAtPlayhead]);

    const showMobileStudio = clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO;
    const isPlayerPlaying = comparePlayer
      ? isPlayingRange
      : (shouldAutoplay || isPlayingRange);

    const handlePlayerProgress = useCallback(({ playedSeconds: played, loaded }) => {
      setPlayedSeconds(played);
      if (Number.isFinite(loaded)) setBuffered(loaded);
      reportPlayheadIfEditing(played);

      const preview = rangePreviewRef.current;
      if (!preview || played < preview.endSec - 0.05) return;

      if (preview.loop) {
        const player = playerRef.current;
        if (player && preview.startSec != null) {
          player.seekTo(preview.startSec, 'seconds');
        }
        return;
      }

      stopClipRangePreview(true);
    }, [reportPlayheadIfEditing, stopClipRangePreview]);

    const handlePlayerSeek = useCallback((seconds) => {
      setPlayedSeconds(seconds);
      reportPlayheadIfEditing(seconds);
    }, [reportPlayheadIfEditing]);

    useEffect(() => {
      rangePreviewRef.current = null;
      setIsPlayingRange(false);
      setIsEditingPlayback(false);
    }, [videoUrl]);

    const editSessionKey = clipEditTarget
      ? `${clipEditTarget.clipIndex}:${clipEditTarget.field}`
      : '';

    useEffect(() => {
      if (!editSessionKey || !playerRef.current || !onClipEditPlayhead) return;
      const formatted = formatClipTimestamp(playerRef.current.getCurrentTime());
      if (formatted) onClipEditPlayhead(formatted);
    }, [editSessionKey, onClipEditPlayhead]);

    useEffect(() => {
      if (clipEditTarget) return;
      setIsEditingPlayback(false);
    }, [clipEditTarget]);

    useEffect(() => {
      const handleKeyPress = (e) => {
        if (!playerRef.current) return;
        const typing = isTypingTarget(e.target);

        // Crop mode: Esc cancels, Enter applies; never mark clips while cropping
        if (isCroppingRef.current && !typing) {
          if (e.key === "Escape") {
            e.preventDefault();
            closeCrop();
            return;
          }
          if (e.key === "Enter") {
            if (e.target?.closest?.("button")) return;
            e.preventDefault();
            applyCropRef.current?.();
            return;
          }
          // arrow keys nudge the crop box when it has focus
          if (e.target?.closest?.(".crop-box")) return;
        }

        if (e.key === " " && !typing) {
          if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) return;
          e.preventDefault();
          togglePlayPause();
          return;
        }

        if (e.key === "Escape" && clipEditTarget && onCancelClipEdit) {
          e.preventDefault();
          onCancelClipEdit();
          return;
        }

        if (e.key === "Enter") {
          if (typing) return;
          if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) return;
          e.preventDefault();
          markClipAtPlayhead();
          return;
        }

        const isArrowSkip =
          ["<", ",", "ArrowLeft"].includes(e.key) || [">", ".", "ArrowRight"].includes(e.key);

        if (isArrowSkip) {
          if (!keyboardSeekEnabled) return;
          if (comparePlayer && !clipEditTarget) return;
          if (typing) return;
          if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO && !clipEditTarget) return;

          e.preventDefault();
          seekPlayheadBySeconds(["<", ",", "ArrowLeft"].includes(e.key) ? -10 : 10);
          return;
        }

        if (typing) return;

        if (clipTouchEnabled && ENABLE_MOBILE_CLIP_STUDIO) return;
      };

      window.addEventListener("keydown", handleKeyPress);
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }, [
      clipEditTarget,
      onCancelClipEdit,
      seekPlayheadBySeconds,
      markClipAtPlayhead,
      togglePlayPause,
      clipTouchEnabled,
      comparePlayer,
      keyboardSeekEnabled,
      closeCrop,
    ]);

    const publishDuration = useCallback((seconds) => {
      const duration = Number(seconds);
      if (!Number.isFinite(duration) || duration <= 0.05) return;
      resolvedDurationRef.current = duration;
      setDuration(duration);
      onDurationReady?.(duration);
    }, [onDurationReady]);

    const handlePlayerReady = useCallback(() => {
      calculateDimensions();
      requestAnimationFrame(calculateDimensions);
      setTimeout(calculateDimensions, 200);
      const readyDuration = playerRef.current?.getDuration?.();
      if (readyDuration > 0.05) publishDuration(readyDuration);
      const coarse = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
      const narrow = window.matchMedia("(max-width: 900px)").matches;
      if ((coarse || narrow) && isYouTubeUrl(videoUrl)) {
        setShowMobilePlay(true);
      }
    }, [calculateDimensions, videoUrl, publishDuration]);

    const handleMobilePlayTap = useCallback((e) => {
      e.stopPropagation();
      setShowMobilePlay(false);
      setShouldAutoplay(true);
      const internal = playerRef.current?.getInternalPlayer();
      if (internal?.playVideo) {
        internal.playVideo();
      } else if (typeof internal?.play === "function") {
        internal.play().catch(() => {});
      }
    }, []);

    // Keep ReactPlayer's `playing` prop in sync with the real state, otherwise
    // it re-applies a stale `playing` after seeks (a paused video would resume).
    const handlePlayerPlay = useCallback(() => {
      setShowMobilePlay(false);
      setIsPlaying(true);
      setShouldAutoplay(true);
      calculateDimensions();
    }, [calculateDimensions]);

    const handlePlayerPause = useCallback(() => {
      setIsPlaying(false);
      setShouldAutoplay(false);
    }, []);

    // ── Custom controls ───────────────────────────────────────────
    /** Custom bar + shield whenever the player is scriptable (YouTube, files). */
    const customControls = playerControllable;

    useEffect(() => {
      setDuration(0);
      setBuffered(0);
      setIsPlaying(false);
      setIsBuffering(false);
    }, [videoUrl]);

    const seekToSeconds = useCallback((seconds, final = true) => {
      const player = playerRef.current;
      if (!player) return;
      const total = duration || player.getDuration?.() || 0;
      const target = Math.max(0, total > 0 ? Math.min(seconds, total) : seconds);
      rangePreviewRef.current = null;
      setIsPlayingRange(false);
      setIsEditingPlayback(false);
      // keepPlaying=true: don't let ReactPlayer pause after seeking
      player.seekTo(target, 'seconds', true);
      setPlayedSeconds(target);
      if (final) reportPlayheadIfEditing(target);
    }, [duration, reportPlayheadIfEditing]);

    const handleToggleMute = useCallback(() => {
      if (muted || volume === 0) {
        setMuted(false);
        if (volume === 0) setVolume(0.6);
      } else {
        setMuted(true);
      }
    }, [muted, volume]);

    const handleVolume = useCallback((v) => {
      setVolume(v);
      setMuted(v === 0);
    }, []);

    const toggleFullscreen = useCallback(() => {
      const el = rootRef.current;
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      } else {
        el.requestFullscreen?.().catch(() => {});
      }
    }, []);

    useEffect(() => {
      const onChange = () => {
        setIsFullscreen(document.fullscreenElement === rootRef.current);
        requestAnimationFrame(calculateDimensions);
      };
      document.addEventListener('fullscreenchange', onChange);
      return () => document.removeEventListener('fullscreenchange', onChange);
    }, [calculateDimensions]);

    const flashTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(flashTimerRef.current), []);

    /** Click on the video itself: play/pause, with a brief centre icon. */
    const handleShieldClick = useCallback(() => {
      if (cropVisible) return;
      setFlash(isPlaying ? 'pause' : 'play');
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlash(null), 520);
      setShowMobilePlay(false);
      togglePlayPause();
    }, [cropVisible, isPlaying, togglePlayPause]);

    const handleShieldDoubleClick = useCallback(() => {
      if (cropVisible) return;
      toggleFullscreen();
    }, [cropVisible, toggleFullscreen]);

    if (playback.kind === 'embed') {
      return (
        <div className="video-player-root video-player-root--embed">
          {playerLabel && (
            <div className="video-player-label">{playerLabel}</div>
          )}
          <div className="video-embed-page">
            <div className="video-embed-page__frame">
              <iframe
                src={playback.url}
                title="Embedded video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="video-embed-page__note">
              Page embed (not YouTube). If the frame is empty, the site may block embedding — use Open in new tab.
              Mark clips with the manual timestamp fields in the Clips panel.
            </p>
            <a
              className="video-embed-page__open"
              href={playback.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </a>
          </div>
        </div>
      );
    }

    const rootClassName = [
      'video-player-root',
      showMobileStudio ? 'video-player-root--mobile-studio' : '',
      comparePlayer ? 'video-player-root--compare' : '',
      cropVisible ? 'is-crop-open' : '',
      cropMode === 'closing' ? 'is-crop-closing' : '',
    ].filter(Boolean).join(' ');

    return (
      <div ref={rootRef} className={rootClassName}>
        {playerLabel && (
          <div className="video-player-label">{playerLabel}</div>
        )}
        <div
          ref={playerWrapperRef}
          className="video-player-wrap"
          style={
            typeof embedSize.height === "number"
              ? { minHeight: embedSize.height, height: embedSize.height }
              : undefined
          }
        >
          <ReactPlayer
            ref={playerRef}
            url={playbackUrl}
            className="video-player-embed"
            controls={!customControls}
            volume={volume}
            muted={muted}
            playbackRate={playbackRate}
            width="100%"
            height="100%"
            playing={isPlayerPlaying}
            playsinline
            config={PLAYER_CONFIG}
            onReady={handlePlayerReady}
            onDuration={publishDuration}
            onPlay={handlePlayerPlay}
            onPause={handlePlayerPause}
            onEnded={handlePlayerPause}
            onProgress={handlePlayerProgress}
            onSeek={handlePlayerSeek}
            onBuffer={() => setIsBuffering(true)}
            onBufferEnd={() => setIsBuffering(false)}
            progressInterval={100}
          />
          {customControls && (
            <div
              className="video-input-shield"
              onClick={handleShieldClick}
              onDoubleClick={handleShieldDoubleClick}
              aria-hidden="true"
            >
              {isBuffering && isPlaying && <span className="video-input-shield__spinner" />}
              {flash && (
                <span key={flash + playedSeconds} className="video-input-shield__flash">
                  {flash === 'play' ? <FaPlay /> : <FaPause />}
                </span>
              )}
            </div>
          )}
          {showMobilePlay && !cropVisible && (
            <button
              type="button"
              className="video-mobile-play-btn"
              onClick={handleMobilePlayTap}
              aria-label="Play video"
            >
              <span className="video-mobile-play-btn__icon" aria-hidden="true" />
            </button>
          )}
          {cropVisible && (
            <CropOverlay
              frame={frameBox}
              rect={cropRect}
              onChange={setCropRect}
              normRatio={normRatio}
              sourceSize={sourceSize}
              interactive={isCropping}
              closing={cropMode === 'closing'}
            />
          )}
        </div>

        {customControls && (
          <PlayerControls
            playing={isPlaying}
            onTogglePlay={togglePlayPause}
            current={playedSeconds}
            duration={duration}
            buffered={buffered}
            onSeek={seekToSeconds}
            onSeekBy={seekPlayheadBySeconds}
            muted={muted}
            volume={volume}
            onToggleMute={handleToggleMute}
            onVolume={handleVolume}
            rate={playbackRate}
            onRate={setPlaybackRate}
            onFullscreen={toggleFullscreen}
            isFullscreen={isFullscreen}
            clips={clips}
            onCrop={comparePlayer ? undefined : () => (isCropping ? closeCrop() : openCrop())}
            cropActive={isCropping}
          />
        )}

        {cropVisible ? (
          <CropPanel
            closing={cropMode === 'closing'}
            compact={clipTouchEnabled}
            aspect={cropAspect}
            onAspectChange={handleAspectChange}
            onReset={resetCrop}
            clips={clips}
            targets={cropTargets}
            onTargetsChange={setCropTargets}
            payload={cropPayload}
            sourceSize={sourceSize}
            onApply={applyCrop}
            onRemove={removeCrop}
            onCopy={copyCrop}
            onCancel={closeCrop}
          />
        ) : showMobileStudio && !comparePlayer ? (
          <div className="video-studio-toolbar">
            <MobileClipControls
              markStep={markStep}
              pendingStart={startTime}
              onSetStart={handleMobileSetStart}
              onSetEnd={handleMobileSetEnd}
              onReset={resetClipMarker}
              disabled={false}
              feedback={mobileFeedback}
            />
          </div>
        ) : null}
      </div>
    );
  }
);

export default Video;
